import axios from 'axios';
import { msalInstance } from '../auth/LoginButton';
import { tokenRequest } from '../auth/msalConfig';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Variable to track if MSAL is initialized
let msalInitialized = false;

// Add auth token to requests
apiClient.interceptors.request.use(async (config: any) => {
  // Ensure MSAL is initialized before using it
  if (!msalInitialized) {
    try {
      await msalInstance.initialize();
      msalInitialized = true;
      console.log('✅ MSAL initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize MSAL:', error);
      // Continue without token - backend will handle unauthorized requests
    }
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    try {
      // Try silent token acquisition using configured scopes from msalConfig
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: accounts[0],
        forceRefresh: false,
      });
      
      config.headers.Authorization = `Bearer ${response.accessToken}`;
      console.log('✅ Auth token added to request (with app roles)');
    } catch (error: any) {
      // Check if it's an interaction_in_progress error
      if (error.errorCode === 'interaction_in_progress') {
        console.warn('⚠️ MSAL interaction already in progress, skipping token request');
      } else {
        console.warn('⚠️ Token acquisition failed, proceeding without token:', error.errorCode || error.message);
      }
      // Don't call acquireTokenRedirect here - it causes problems when called from API interceptor
      // Instead, let the request proceed without token
      // If auth is required, backend will return 401 and the user will need to login again
    }
  } else {
    console.warn('⚠️ No MSAL accounts found - user may need to login');
  }
  
  // Add user headers from sessionStorage (set by LoginButton)
  const storedUser = sessionStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      if (user.email) {
        config.headers['x-dev-user-id'] = user.email;
        config.headers['x-dev-role'] = user.role || 'Survey.Creator';
      }
    } catch (e) {
      console.warn('⚠️ Failed to parse user from sessionStorage:', e);
    }
  }
  
  return config;
});

// Export the configured apiClient for use in other components
export { apiClient };

export interface JobStatus {
  id: string;
  userId: string;
  userEmail: string;
  fileName: string;
  templateId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  errorCount: number;
  errors: Array<{ row: number; email: string; error: string }>;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface Campaign {
  id: string;
  survey_id: string;
  title: string;
  status: string;
  target_audience: string;
  total_recipients: number;
  emails_sent: number;
  responses_received: number;
  created_at: string;
  sent_at: string | null;
  creator_id?: string;
  ownerId?: string;
}

export interface CampaignRecipient {
  id?: string;
  student_id?: string;
  student_name?: string;
  student_email?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  email_sent: boolean;
  email_send_count?: number;
  survey_completed: boolean;
  sent_at?: string | null;
  completed_at?: string | null;
  email_sent_at?: string | null;
}

export interface CampaignDetails {
  id: string;
  survey_id: string;
  title: string;
  name?: string;
  status: string;
  creator_id: string;
  target_audience: string;
  email_template: string;
  template_name?: string;
  template_html?: string;
  template_subject?: string;
  subject_line: string;
  from_email: string;
  from_name: string;
  recipients: CampaignRecipient[];
  created_at: string;
  sent_at: string | null;
  campaign_type?: string;
  communication_channels?: string[];
  modo_title?: string;
  modo_message?: string;
  retrigger_log?: Array<{ triggered_by: string; triggered_at: string; modo_message_id: string }>;
  last_error?: string;
}

export interface UploadResponse {
  campaignId: string;
  message: string;
  fileName: string;
  recipientCount: number;
}

// Upload CSV and create job
export const uploadCampaign = async (
  file: File,
  campaignName: string,
  templateId: string,
  templateName: string,
  fromEmail: string,
  fromName: string,
  surveyId?: string,
  isTemplate?: boolean,
  campaignType?: string,
  extraFields?: Record<string, string>
): Promise<UploadResponse> => {
  console.log('📤 Uploading campaign:', { 
    fileName: file.name, 
    fileSize: file.size,
    campaignName,
    templateId,
    templateName,
    fromEmail, 
    fromName,
    campaignType 
  });

  const formData = new FormData();
  formData.append('file', file);
  formData.append('campaignName', campaignName);
  formData.append('templateId', templateId);
  formData.append('templateName', templateName);
  formData.append('fromEmail', fromEmail);
  formData.append('fromName', fromName);
  if (surveyId) {
    formData.append('surveyId', surveyId);
  }
  if (isTemplate) {
    formData.append('isTemplate', 'true');
  }
  if (campaignType) {
    formData.append('campaignType', campaignType);
  }
  if (extraFields) {
    for (const [key, value] of Object.entries(extraFields)) {
      formData.append(key, value);
    }
  }

  try {
    const response = await apiClient.post<UploadResponse>('/upload-campaign', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('✅ Campaign uploaded successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Upload failed:', error.response?.data || error.message);
    throw error;
  }
};

// Get job status
export const getJobStatus = async (jobId: string): Promise<JobStatus> => {
  const response = await apiClient.get<JobStatus>(`/job-status/${jobId}`);
  return response.data;
};

// Get all user's campaigns (backend automatically filters by authenticated user)
export const getUserJobs = async (): Promise<Campaign[]> => {
  // Backend filters by authenticated user's ID from token
  // Survey.Creator users will only see their own campaigns
  // Survey.Admin and Campaign.Manager users will see all campaigns
  console.log('📋 Loading campaigns (backend will filter based on auth token)');
  try {
    const response = await apiClient.get<Campaign[]>('/campaigns');
    console.log('📋 Campaigns response:', response);
    console.log('📋 Response data type:', typeof response.data);
    console.log('📋 Response data is array:', Array.isArray(response.data));
    
    // Defensive check - ensure we have an array
    if (Array.isArray(response.data)) {
      console.log('📋 Campaigns loaded, count:', response.data.length);
      return response.data;
    } else if (response.data && (response.data as any).error) {
      console.error('📋 API returned error:', (response.data as any).error);
      throw new Error((response.data as any).error);
    } else {
      console.error('📋 Unexpected response format:', response.data);
      throw new Error('Unexpected response format from server');
    }
  } catch (error) {
    console.error('📋 Error loading campaigns:', error);
    throw error;
  }
};

// Get campaign details
export const getCampaignDetails = async (campaignId: string): Promise<CampaignDetails> => {
  const response = await apiClient.get<CampaignDetails>(`/campaigns/${campaignId}`);
  return response.data;
};

// Update campaign
export const updateCampaign = async (campaignId: string, data: { title: string; email_subject: string; email_template: string }): Promise<void> => {
  await apiClient.put(`/campaigns/${campaignId}`, data);
};

// Archive campaign (soft delete)
export const archiveCampaign = async (campaignId: string): Promise<void> => {
  await apiClient.post(`/campaigns/${campaignId}/archive`);
};

// Delete a campaign permanently
export const deleteCampaign = async (campaignId: string): Promise<void> => {
  await apiClient.delete(`/campaigns/${campaignId}`);
};

// Resend email to a specific recipient
export const resendEmail = async (campaignId: string, recipientEmail: string): Promise<void> => {
  await apiClient.post(`/campaigns/${campaignId}/resend`, { recipient_email: recipientEmail });
};

// Bulk resend emails to all unsent recipients
export const bulkResendEmails = async (campaignId: string): Promise<{ message: string; sent_count: number; failed_count: number }> => {
  const response = await apiClient.post(`/campaigns/${campaignId}/resend`, { resend_to: 'all' });
  return response.data;
};

// Resend emails to selected recipients
export const resendToSelected = async (campaignId: string, recipientEmails: string[]): Promise<{ sent_count: number; failed_count: number }> => {
  const response = await apiClient.post(`/campaigns/${campaignId}/resend`, { recipient_emails: recipientEmails });
  return response.data;
};

// Retrigger Modo push notification for an existing modo_push campaign
export const retriggerPush = async (campaignId: string): Promise<{ message: string; modo_message_id: string; triggered_at: string }> => {
  const response = await apiClient.post(`/campaigns/${campaignId}/retrigger-push`, {});
  return response.data;
};

// Update campaign status (archive, unarchive, restore)
export const updateCampaignStatus = async (campaignId: string, status: 'active' | 'archived' | 'deleted'): Promise<void> => {
  await apiClient.patch(`/campaigns/${campaignId}/status`, { status });
};

// Cancel a running or stuck campaign to stop further emails
export const cancelCampaign = async (campaignId: string): Promise<{ message: string; emails_sent_so_far: number; total_recipients: number }> => {
  const response = await apiClient.post(`/campaigns/${campaignId}/cancel`);
  return response.data;
};

export interface SurveyResponse {
  id: string;
  survey_id: string;
  user_id?: string;
  respondent_name?: string;
  respondent_email?: string;
  answers: Array<{
    question_id: string;
    question_text: string;
    answer: any;
  }>;
  submitted_at: string;
  ip_address?: string;
  user_agent?: string;
}

export const getCampaignResponses = async (campaignId: string): Promise<SurveyResponse[]> => {
  const response = await apiClient.get(`/campaigns/${campaignId}/responses`);
  return response.data;
};

// Check for duplicate recipients and count issues
export const checkCampaignDuplicates = async (campaignId: string): Promise<{
  campaign_id: string;
  campaign_title: string;
  stored_counts: {
    total_recipients: number;
    emails_sent: number;
    emails_failed: number;
  };
  actual_counts: {
    total_recipient_records: number;
    sent_records: number;
    unsent_records: number;
    unique_emails_total: number;
    unique_sent_emails: number;
    unique_unsent_emails: number;
  };
  duplicates: {
    has_duplicates: boolean;
    duplicate_count: number;
    duplicate_details: Array<{
      email: string;
      count: number;
      sent_status: boolean[];
      sent_dates: (string | null)[];
    }>;
  };
  unsent_sample: Array<{
    email: string;
    firstName: string;
    lastName: string;
    lastError?: string;
  }>;
  recommendations: string[];
}> => {
  const response = await apiClient.get(`/campaigns/${campaignId}/check-duplicates`);
  return response.data;
};

// Fix campaign counts to match actual unique sent emails
export const fixCampaignCounts = async (campaignId: string): Promise<{
  message: string;
  old_counts: {
    emails_sent: number;
    total_recipients: number;
  };
  new_counts: {
    emails_sent: number;
    total_recipients: number;
  };
}> => {
  const response = await apiClient.post(`/campaigns/${campaignId}/fix-counts`);
  return response.data;
};

// Get job report (detailed)
export const getJobReport = async (jobId: string): Promise<Blob> => {
  const response = await apiClient.get(`/job-report/${jobId}`, {
    responseType: 'blob',
  });
  return response.data;
};

// ── Population fetching ──────────────────────────────────────────────────────

export type PopulationType =
  | 'current_students'
  | 'withdrawn_students'
  | 'alumni'
  | 'faculty'
  | 'staff'
  | 'it_systems_devops';

export interface PopulationRecipient {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  department?: string;
}

/**
 * Fetch recipients for one or more population types.
 * Calls the backend /api/population endpoint for each type and deduplicates by email.
 */
export const resolveDistributionList = async (
  email: string
): Promise<{ groupName: string; members: PopulationRecipient[] }> => {
  const response = await apiClient.get<{ groupName: string; members: PopulationRecipient[] }>(
    `/resolve-distribution-list?email=${encodeURIComponent(email)}`
  );
  console.log(
    `📋 resolveDistributionList(${email}) → "${response.data.groupName}" with ${response.data.members.length} members`
  );
  return response.data;
};

export const fetchPopulation = async (
  types: PopulationType[]
): Promise<PopulationRecipient[]> => {
  const results = await Promise.all(
    types.map((t) =>
      apiClient
        .get<PopulationRecipient[]>(`/population?type=${t}`)
        .then((r) => r.data)
        .catch((err) => {
          console.error(`❌ Failed to fetch population "${t}":`, err);
          throw err;
        })
    )
  );

  // Flatten and deduplicate by email (case-insensitive)
  const seen = new Set<string>();
  const combined: PopulationRecipient[] = [];
  for (const group of results) {
    for (const person of group) {
      const key = person.email.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        combined.push(person);
      }
    }
  }

  console.log(
    `👥 fetchPopulation(${types.join(', ')}) → ${combined.length} unique recipients`
  );
  return combined;
};

export default apiClient;
